import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { queryClient, trpc } from "~/utils/trpc";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@quake/shared-ui/components/ui/dialog";
import { Button } from "@quake/shared-ui/components/ui/button";
import { Label } from "@quake/shared-ui/components/ui/label";
import { Checkbox } from "@quake/shared-ui/components/ui/checkbox";
import { Switch } from "@quake/shared-ui/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@quake/shared-ui/components/ui/card";
import { toast } from "@quake/shared-ui/lib/sonner";
import { UserProfileForm, ProfileFormHandle } from "~/components/profile/UserProfileForm";
import Loader2Icon from "lucide-react/dist/esm/icons/loader-2";
import ShieldOffIcon from "lucide-react/dist/esm/icons/shield-off";
import UserIcon from "lucide-react/dist/esm/icons/user";
import { CheckPermission } from "~/components/CheckPermission";
import { hasPermission, useHasPermission } from "~/store/usePermissionStore";

export const Route = createFileRoute("/_dashboard/$datasetSlug/settings/users/$id/edit")({
  loader: async ({ params, parentMatchPromise }) => {
    await parentMatchPromise;
    const session = await queryClient.fetchQuery(trpc.auth.getSession.queryOptions()).catch(() => null);

    if (!hasPermission({ role: session?.user?.role, permissions: session?.user?.permissions }, "users", "update")) {
      throw redirect({ to: "/$datasetSlug/dashboard", params: { datasetSlug: params.datasetSlug } });
    }
  },
  component: EditUserProfileComponent,
});

function EditUserProfileComponent() {
  const { id, datasetSlug } = Route.useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const formRef = useRef<ProfileFormHandle>(null);
  const canDeactivateUsers = useHasPermission("users", "deactivate");

  const userQuery = useQuery(trpc.users.getDatasetUserById.queryOptions({ id }));
  const userDatasetId = userQuery.data?.id ?? id;
  const isCurrentlyActive = userQuery.data?.is_active !== false && userQuery.data?.dataset_is_active !== false;
  const [accountAccess, setAccountAccess] = useState<{
    userDatasetId: string;
    deactivateAccess: boolean;
    wasActive: boolean;
  } | null>(null);

  useEffect(() => {
    const loadedUserDatasetId = userQuery.data?.id;
    if (!loadedUserDatasetId || accountAccess?.userDatasetId === loadedUserDatasetId) return;

    setAccountAccess({
      userDatasetId: loadedUserDatasetId,
      deactivateAccess: !isCurrentlyActive,
      wasActive: isCurrentlyActive,
    });
  }, [accountAccess?.userDatasetId, isCurrentlyActive, userQuery.data?.id]);

  const accountAccessForUser = accountAccess?.userDatasetId === userDatasetId ? accountAccess : null;

  const updateUser = useMutation(
    trpc.users.updateDatasetUser.mutationOptions({
      onSuccess: () => {
        qc.invalidateQueries(trpc.users.getUsersByDatasets.queryFilter());
        qc.invalidateQueries({ queryKey: trpc.users.getDatasetUserById.queryOptions({ id }).queryKey });
      },
    }),
  );

  const activateUser = useMutation(
    trpc.datasets.activateUser.mutationOptions({
      onSettled: () => {
        qc.invalidateQueries(trpc.users.getUsersByDatasets.queryFilter());
        qc.invalidateQueries({ queryKey: trpc.users.getDatasetUserById.queryOptions({ id }).queryKey });
      },
    }),
  );

  const deactivateUser = useMutation(
    trpc.datasets.deactivateUser.mutationOptions({
      onSettled: () => {
        qc.invalidateQueries(trpc.users.getUsersByDatasets.queryFilter());
        qc.invalidateQueries({ queryKey: trpc.users.getDatasetUserById.queryOptions({ id }).queryKey });
      },
    }),
  );

  const deactivateAccess = accountAccessForUser?.deactivateAccess ?? false;
  const [sendWelcomeEmail, setSendWelcomeEmail] = useState(true);
  const wasActive = accountAccessForUser?.wasActive ?? false;

  const initialData = useMemo(() => {
    if (!userQuery.data) return null;
    const {
      first_name,
      last_name,
      name,
      profile_photo,
      middle_name,
      notification_email,
      email,
      phone_numbers,
      avatar_color,
    } = userQuery.data;

    let finalFirstName = first_name;
    let finalLastName = last_name;
    if (!finalFirstName && name) {
      const parts = name.split(" ");
      finalFirstName = parts[0] ?? "";
      if (parts.length > 1) finalLastName = parts.slice(1).join(" ");
    }

    return {
      profile_photo: profile_photo ?? "",
      first_name: finalFirstName ?? "",
      middle_name: middle_name ?? "",
      last_name: finalLastName ?? "",
      notification_email: notification_email ?? email ?? "",
      phone_numbers: phone_numbers ?? [],
      avatar_color: avatar_color ?? null,
    };
  }, [userQuery.data]);

  const willBeActive = !deactivateAccess;
  const accessChanged = canDeactivateUsers && wasActive !== willBeActive;
  const isPending = updateUser.isPending || activateUser.isPending || deactivateUser.isPending;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isPending) return;

    const form = formRef.current;
    const profileValid = form?.isValid() ?? false;
    const canSubmit = profileValid || accessChanged;

    if (!canSubmit) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      if (profileValid && form && initialData) {
        const values = form.getValues();
        const photoChanged = values.profile_photo !== initialData.profile_photo;
        const colorChanged = values.avatar_color !== initialData.avatar_color;
        await updateUser.mutateAsync({
          id: userDatasetId,
          first_name: values.first_name || null,
          middle_name: values.middle_name || null,
          last_name: values.last_name || null,
          notification_email: values.notification_email || null,
          phone_numbers: values.phone_numbers.filter((p) => p.number.trim()),
          ...(photoChanged ? { profile_photo: values.profile_photo || null } : {}),
          ...(colorChanged ? { avatar_color: values.avatar_color } : {}),
        });
      }

      if (accessChanged) {
        if (willBeActive) {
          await activateUser.mutateAsync({ user_dataset_id: userDatasetId, send_welcome_email: sendWelcomeEmail });
        } else {
          await deactivateUser.mutateAsync({ user_dataset_id: userDatasetId });
        }
      }

      navigate({ to: `/${datasetSlug}/settings/users` });
      toast.success("Changes saved.");
    } catch (error: unknown) {
      toast.error(error instanceof Error ? error.message : "Failed to save changes.");
    }
  };

  if (userQuery.isLoading || !initialData || !accountAccessForUser) {
    return (
      <Dialog open>
        <DialogContent className="flex items-center justify-center p-12">
          <div className="flex flex-col items-center gap-4">
            <Loader2Icon className="h-8 w-8 motion-preset-spin text-blue-500" />
            <p className="text-sm text-gray-500">Loading user profile...</p>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open onOpenChange={() => navigate({ to: `/${datasetSlug}/settings/users` })}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto max-md:w-[calc(100%-1rem)] max-md:rounded-lg max-md:p-4">
        <DialogHeader className="pb-4 max-md:pr-6">
          <DialogTitle className="text-2xl font-bold flex items-center gap-2">
            <UserIcon className="h-6 w-6 text-blue-500" />
            Edit User Profile
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-3">
          <UserProfileForm
            key={id}
            ref={formRef}
            initialData={initialData}
            accountEmail={userQuery.data.email}
            fallbackImageUrl={userQuery.data.profile_image}
          />

          {canDeactivateUsers ? (
            <Card className="border border-destructive/40 bg-destructive/5 dark:bg-destructive/10">
              <CardHeader className="pb-1">
                <CardTitle className="text-base flex items-center gap-2 text-destructive">
                  <ShieldOffIcon className="h-4 w-4" />
                  Account Access
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-2 space-y-3">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-2">
                    <Switch
                      id="deactivate_access"
                      checked={deactivateAccess}
                      onCheckedChange={(checked) =>
                        setAccountAccess((current) => (current ? { ...current, deactivateAccess: checked } : current))
                      }
                    />
                    <Label htmlFor="deactivate_access" className="cursor-pointer font-medium">
                      Deactivate Access
                    </Label>
                  </div>
                  <p className="text-xs text-muted-foreground pl-[52px]">
                    {deactivateAccess
                      ? "This user cannot log in. Uncheck to restore access."
                      : "Revokes login access immediately. Their profile and data are kept."}
                  </p>
                </div>
                {!wasActive && !deactivateAccess && (
                  <div className="flex flex-col gap-1 pl-6">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="send_welcome_email"
                        checked={sendWelcomeEmail}
                        onCheckedChange={(checked) => setSendWelcomeEmail(!!checked)}
                      />
                      <Label htmlFor="send_welcome_email" className="cursor-pointer text-sm">
                        Send welcome email
                      </Label>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : null}

          <DialogFooter className="flex flex-col sm:flex-row gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate({ to: `/${datasetSlug}/settings/users` })}
              className="w-full sm:w-auto order-2 sm:order-1"
              disabled={isPending}
            >
              Cancel
            </Button>
            <CheckPermission resource="users" action="update">
              <Button type="submit" disabled={isPending} className="w-full sm:w-auto order-1 sm:order-2">
                {isPending ? (
                  <>
                    <Loader2Icon className="mr-2 h-4 w-4 motion-preset-spin" />
                    Saving Changes...
                  </>
                ) : (
                  "Save Changes"
                )}
              </Button>
            </CheckPermission>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
